import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { Button, Header, Preloader, Title, Breadcrumbs } from "../../../components";
import { SubscrItem } from "../components";
import { ITempBasketItem } from "../components/SubscrItem/types";

import { useAppDispatch } from "../../../redux/store";
import { fetchGetSubscriptions } from "../../../redux/subscriptions/asyncActions";
import { subscriptionsSelector } from "../../../redux/subscriptions/selectors";
import { isAuthenticatedSelector } from "../../../redux/profile/authAndReg/selectors";
import { setShowWindow } from "../../../redux/profile/authAndReg/slice";
import { WindowNames } from "../../../resources/constants";
import { LoginModal } from "../../../components";

import styles from "./Subscriptions.module.scss";

import { SubscrPropsType } from "./types";
import { ISubscription } from "../../../redux/subscriptions/types";
import {
  removeSelectedAdultSubscrFromStorage,
  removeSelectedFamilySubscrFromStorage,
  removeSelectedKidSubscrFromStorage,
  removeSubscrFormsDataFromStorage,
} from "../../../utils";
import {
  resetSelectedAdultSubscr,
  resetSelectedFamilySubscr,
  resetSelectedKidSubscr,
  resetSubscrFormsData,
  setSelectedAdultSubscr,
  setSelectedFamilySubscription,
  setSelectedKidSubscr,
} from "../../../redux/subscriptions/slice";
import {
  setSelectedAdultSubscrToStorage,
  setSelectedFamilySubscrToStorage,
  setSelectedKidSubscrToStorage,
} from "../../../utils";


export const Subscriptions: React.FC<SubscrPropsType> = ({ title }) => {
  const { subscriptions, isLoading } = useSelector(subscriptionsSelector);
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const location = useLocation();
  const [tempBasket, setTempBasket] = React.useState<ITempBasketItem[]>([]);
  const [isLoginModalOpen, setIsLoginModalOpen] = React.useState(false);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  React.useEffect(() => {
    removeSelectedAdultSubscrFromStorage();
    removeSelectedKidSubscrFromStorage();
    removeSelectedFamilySubscrFromStorage();
    removeSubscrFormsDataFromStorage();
    dispatch(resetSubscrFormsData());
    dispatch(resetSelectedAdultSubscr());
    dispatch(resetSelectedKidSubscr());
    dispatch(resetSelectedFamilySubscr());
    dispatch(fetchGetSubscriptions());
  }, []);

  const addToTempBasket = React.useCallback((
    subscription: ISubscription,
    quantity: number,
    adultsCount: number | null = null,
    childrenCount: number | null = null
  ) => {
    setTempBasket((prev) => {
      const filtered = prev.filter((sub) => sub.id !== subscription.id);

      let next = filtered;

      if (subscription.type === "family") {
        if (adultsCount && adultsCount > 0 && adultsCount + (childrenCount || 0) >= 3) {
          const totalPeople = adultsCount + (childrenCount || 0);
          const basePrice = (subscription as any).basePrice || subscription.price;
          const additionalPersonPrice = (subscription as any).additionalPersonPrice || 1000;
          const calculatedPrice = basePrice + (totalPeople - 2) * additionalPersonPrice;

          const newItem: ITempBasketItem = {
            id: subscription.id,
            type: subscription.type,
            quantity: 1,
            adultsCount,
            childrenCount: childrenCount || 0,
            totalPeople,
            price: calculatedPrice,
            subscription,
          };
          next = [...filtered, newItem];
        } else {
          next = filtered;
        }
      } else {
        const subType = (subscription as any).mockType || (subscription as any).type || subscription.type;
        
        if (subType === "gift" && quantity > 0) {
          next = filtered.filter((sub) => {
            const basketSubType = (sub as any).type || sub.type;
            return basketSubType === "child";
          });
          const newItem: ITempBasketItem = {
            id: subscription.id,
            type: "gift",
            quantity,
            adultsCount: undefined,
            childrenCount: undefined,
            totalPeople: undefined,
            price: subscription.price,
            subscription,
          };
          next = [...next, newItem];
        } else if (subType === "adult" || subType === "kid" || subType === "daily") {
          if (quantity > 0) {
            const newItem: ITempBasketItem = {
              id: subscription.id,
              type: subType,
              quantity,
              adultsCount: undefined,
              childrenCount: undefined,
              totalPeople: undefined,
              price: subscription.price,
              subscription,
            };
            next = [...filtered, newItem];
          } else {
            next = filtered;
          }
        } else if (quantity > 0) {
          const newItem: ITempBasketItem = {
            id: subscription.id,
            type: subscription.type,
            quantity,
            adultsCount: null,
            childrenCount: null,
            totalPeople: null,
            price: subscription.price,
            subscription,
          };
          next = [...filtered, newItem];
        } else {
          next = filtered;
        }
      }

      const hasAdult = next.some((sub) => sub.type === "adult");
      const hasFamily = next.some((sub) => sub.type === "family");
      if (!hasAdult && !hasFamily) {
        next = next.filter((sub) => sub.type !== "child");
      }

      return next;
    });
  }, []);

  const getSubscriptionStatus = React.useCallback(
    (subscription: ISubscription): "enabled" | "disabled" => {
      const subType = (subscription as any).mockType || (subscription as any).type || subscription.type;
      const hasFamily = tempBasket.some((sub) => {
        const basketSubType = (sub as any).type || sub.type;
        return basketSubType === "family";
      });
      const hasGift = tempBasket.some((sub) => {
        const basketSubType = (sub as any).type || sub.type;
        return basketSubType === "gift";
      });
      const hasDaily = tempBasket.some((sub) => {
        const basketSubType = (sub as any).type || sub.type;
        return basketSubType === "daily";
      });
      const hasAdult = tempBasket.some((sub) => {
        const basketSubType = (sub as any).type || sub.type;
        return basketSubType === "adult";
      });
      const hasChild = tempBasket.some((sub) => {
        const basketSubType = (sub as any).type || sub.type;
        return basketSubType === "child";
      });

      if (hasFamily && subType !== "family") {
        return "disabled";
      }

      if (hasGift && subType !== "gift") {
        return "disabled";
      }

      if (hasDaily && subType !== "daily") {
        return "disabled";
      }

      if (subType === "child" && !hasAdult && !hasFamily) {
        return "disabled";
      }

      const selectionType = (subscription as any).selectionType;
      if (selectionType === "exclusive") {
        if (subType === "family") {
          return hasGift || hasDaily ? "disabled" : "enabled";
        }
        if (subType === "gift") {
          const hasNonGiftNonChildItems = tempBasket.some(
            (sub) => sub.type !== "gift" && sub.type !== "child"
          );
          return hasNonGiftNonChildItems ? "disabled" : "enabled";
        }
        if (subType === "daily") {
          const hasNonDailyItem = tempBasket.some((sub) => sub.type !== "daily");
          return hasFamily || hasGift || hasNonDailyItem ? "disabled" : "enabled";
        }
      }

      return "enabled";
    },
    [tempBasket]
  );

  const validateSelection = (): boolean => {
    const childSubscriptions = tempBasket.filter((sub) => sub.type === "child");
    const adultSubscriptions = tempBasket.filter((sub) => sub.type === "adult");

    if (childSubscriptions.length > 0 && adultSubscriptions.length === 0) {
      alert(
        "Ошибка выбора: Детские абонементы можно приобрести только вместе со взрослыми абонементами"
      );
      return false;
    }

    const familySubscription = tempBasket.find((sub) => sub.type === "family");
    if (familySubscription && (familySubscription.totalPeople || 0) < 3) {
      alert("Ошибка выбора: Для семейного абонемента требуется минимум 3 человека");
      return false;
    }

    return true;
  };

  const handleNextPage = () => {
    if (!isAuthenticated) {
      dispatch(setShowWindow(WindowNames.AUTH));
      setIsLoginModalOpen(true);
      return;
    }

    if (!validateSelection()) {
      return;
    }

    dispatch(resetSelectedAdultSubscr());
    dispatch(resetSelectedKidSubscr());
    dispatch(resetSelectedFamilySubscr());

    const adultSubscr: any[] = [];
    const kidSubscr: any[] = [];
    const familySubscr: any[] = [];

    tempBasket.forEach((item) => {
      if (item.type === "family") {
        const familyItem = {
          subscription: item.subscription,
          quantity: [item.adultsCount || 0, item.childrenCount || 0],
          profilesCount: {
            adult: item.adultsCount || 0,
            kid: item.childrenCount || 0,
          },
        };
        familySubscr.push(familyItem);
        dispatch(setSelectedFamilySubscription(familyItem));
      } else if (item.type === "kid" || item.type === "child") {
        const kidItem = {
          subscription: item.subscription,
          quantity: item.quantity || 0,
        };
        kidSubscr.push(kidItem);
        dispatch(setSelectedKidSubscr(kidItem));
      } else {
        const adultItem = {
          subscription: item.subscription,
          quantity: item.quantity || 0,
        };
        adultSubscr.push(adultItem);
        dispatch(setSelectedAdultSubscr(adultItem));
      }
    });

    setSelectedAdultSubscrToStorage(JSON.stringify(adultSubscr));
    setSelectedKidSubscrToStorage(JSON.stringify(kidSubscr));
    setSelectedFamilySubscrToStorage(JSON.stringify(familySubscr));

    navigate("/subscriptions-forms");
  };

  const handleQuantityChange = React.useCallback(
    (subscriptionId: string | number, quantity: number) => {
      const subscription = subscriptions.find((sub) => String(sub.id) === String(subscriptionId));
      if (subscription) {
        addToTempBasket(subscription, quantity);
      }
    },
    [subscriptions, addToTempBasket]
  );

  const handleFamilyCountChange = React.useCallback(
    (subscriptionId: string | number, adultsCount: number, childrenCount: number) => {
      const subscription = subscriptions.find((sub) => String(sub.id) === String(subscriptionId));
      if (subscription) {
        addToTempBasket(subscription, 1, adultsCount, childrenCount);
      }
    },
    [subscriptions, addToTempBasket]
  );

  const subscriptionsList = rendersubscriptionsList(subscriptions);

  function rendersubscriptionsList(subscriptions: ISubscription[]) {
    if (!subscriptions || subscriptions.length === 0) {
      return <div>Абонементы не найдены</div>;
    }
    return (
      <div className={styles.subscrItems}>
        {subscriptions.map((item: ISubscription, i: number) => {
          const status = getSubscriptionStatus(item);
          const isDisabled = status === "disabled";
          const selectedItem = tempBasket.find((sub) => sub.id === item.id);

          return (
            <SubscrItem
              key={item.id || i}
              data={item}
              onQuantityChange={handleQuantityChange}
              onFamilyCountChange={handleFamilyCountChange}
              selectedSubscriptions={tempBasket}
              disabled={isDisabled}
            />
          );
        })}
      </div>
    );
  }

  return (
    <>
      <Header title={title} />
      <main>
        <div className="container">
          <Breadcrumbs />
        </div>
        <section className="container">
          <Title>{title}</Title>
          {isLoading ? <Preloader /> : subscriptionsList}
          {tempBasket.length > 0 && (
            <Button
              newStyles={styles.button}
              onClickFunc={handleNextPage}
            >
              Далее
            </Button>
          )}
        </section>
      </main>
      {isLoginModalOpen && (
        <LoginModal isOpen={isLoginModalOpen} setIsOpen={setIsLoginModalOpen} />
      )}
    </>
  );
};
